/**
 * Client-side photo curation, no server round-trip : the whole pipeline runs in the browser on the
 * thumbnails the editor already has.
 *
 *  1. analyse   : curator.ImageAnalyzer (dHash, sharpness, exposure, colorfulness) and
 *                 curator.FaceFinder (MediaPipe BlazeFace) on every photo, one at a time, yielding
 *                 to the UI between photos
 *  2. reject    : blurry (relative to the set), too dark / bright, tiny, documents
 *  3. dedupe    : exact and burst duplicates by hash distance, the best scored twin survives
 *  4. group     : curator.MomentClustering on capture time and GPS
 *  5. cover     : the best scored photo, faces preferred
 *
 * Results are also written on each photo : photo.curation = { score, metrics, error } and the faces
 * go through models.Photo.setNormalizedFaces() so the stage centers crops on them as before.
 *
 * The working canvases are kept until release() so cropFor() can run smartcrop on them.
 *
 * Events (on this service) :
 *  "curate-progress" { step : "analysing" | "sorting", progress : 0..100, index, total }
 *  "curate-finished" result
 */
fx.service(class PhotoCurator extends fx.Service
{
    constructor(model)
    {
        super();

        this.model = model.creationModel;

        let settings = fx.get("settings") || {};

        this.analyzer = new curator.ImageAnalyzer({ size : settings.curatorImageSize || 256 });

        this.faces = new curator.FaceFinder({
            bundleUrl : settings.curatorFaceBundleUrl,
            wasmPath : settings.curatorFaceWasmPath,
            modelUrl : settings.curatorFaceModelUrl,
            minConfidence : settings.curatorFaceMinConfidence || 0.6,
            minSuppression : settings.curatorFaceMinSuppression || 0.25,
            minArea : settings.curatorFaceMinArea,
            checkKeypoints : settings.curatorFaceCheckKeypoints
        });

        this.cropper = new curator.SmartCropper();

        this.clustering = new curator.MomentClustering({ maxGroupSize : settings.curatorMaxGroupSize || 12 });

        this._entries = {};
        this._running = false;

        this._queue = [];        // photos waiting for a background analysis (prepare)
        this._draining = false;
        this._watching = false;
    }

    get running()
    {
        return this._running;
    }

    get defaults()
    {
        return {
            duplicateDistance : 3,      // hash bits : same picture whatever the time
            burstDistance : 10,         // hash bits : same shot when taken within burstSeconds
            burstSeconds : 120,
            minSharpness : 12,          // Laplacian variance floor...
            relativeSharpness : 0.12,   // ...and relative to the median of the set
            minBrightness : 0.06,
            maxBrightness : 0.96,
            maxClippedDark : 0.6,
            maxClippedBright : 0.5,
            minSize : 250,              // px, either side
            maxRejectRatio : 0.3,       // never drop more than 30% of the set
            maxGroups : 0,              // 0 : no bound
            detectFaces : true
        };
    }

    /**
     * @param photos   models.Photo[] (or ModelArray)
     * @param options  overrides of defaults
     * @returns Promise<{ kept, rejected : [{ photo, reasons }], duplicates : [{ photo, reasons, twin }], groups, cover, analysed }>
     */
    curate(photos, options)
    {
        let opts = Object.assign({}, this.defaults, options || {});
        let list = (photos && photos.toArray) ? photos.toArray() : (photos || []).slice();
        list = list.filter((photo) => photo && !photo.deleted);

        this.release();
        this._running = true;

        return this._analyseAll(list, opts)
            .then((entries) => {
                this._progress("sorting", 100, entries.length, entries.length);

                let result = this._select(entries, opts);

                result.groups = this.clustering.cluster(result.kept, opts.maxGroups);
                result.cover = this._pickCover(result.kept);
                result.analysed = entries.length;

                this._running = false;
                this.trigger("curate-finished", result);

                return result;
            })
            .catch((e) => {
                this._running = false;
                throw e;
            });
    }

    // ---------------------------- background ---------------------------- //

    /** Faces looked for already : found by the browser (even none), or stored with the photo. */
    facesKnown(photo)
    {
        let faces = photo ? photo.faces : null;
        let list = faces ? faces.faces : null;   // an array, or a ModelArray when stored with the photo

        // "mediapipe" : looked for in the browser ; "none" : detection unavailable here, nothing to retry
        return !!(list && ((list.length || 0) > 0 || faces.source == "mediapipe" || faces.source == "none"));
    }

    /** Metrics and faces already on the photo : the curation reuses them instead of analysing again. */
    analysed(photo)
    {
        let curation = photo ? photo.curation : null;

        return !!(curation && curation.metrics && !curation.error && this.facesKnown(photo));
    }

    /**
     * Analyses photos in the background (metrics, faces) as they are added to the album, one at a
     * time and only those not analysed yet, so that a later curation skips them. Waits while a
     * curation runs (it analyses what it needs itself).
     */
    prepare(photos)
    {
        let list = (photos && photos.toArray) ? photos.toArray() : (photos || []).slice();

        for (let i = 0; i < list.length; i++)
        {
            let photo = list[i];
            if (photo && !photo.deleted && !this.analysed(photo) && this._queue.indexOf(photo) == -1)
                this._queue.push(photo);
        }

        this._drain();
    }

    /** Analyses every photo of the album that is not yet, as they are added (extraction, phone). */
    watch()
    {
        if (this._watching)
            return;

        this._watching = true;

        let onPhoto = (e) => {
            let photo = e ? e.detail : null;
            if (photo && photo instanceof models.Photo)
                this.prepare([photo]);
        };

        this.on("photo-extracted", onPhoto, fx.service("MediasUploader"));
        this.on("photo-extracted", onPhoto, fx.service("GoogleService"));
        this.on("mobile-upload-photo", onPhoto, fx.service("SmsService"));
    }

    _drain()
    {
        if (this._draining)
            return;

        this._draining = true;

        let next = () => {
            if (!this._queue.length)
            {
                this._draining = false;
                return;
            }

            // a curation is running : it has priority and analyses its photos itself
            if (this._running)
            {
                setTimeout(next, 500);
                return;
            }

            let photo = this._queue.shift();
            if (photo.deleted || this.analysed(photo))
            {
                next();
                return;
            }

            this._analysePhoto(photo, { detectFaces : true })
                .then((entry) => {
                    // results live on the photo (curation, faces) : no working canvas kept around
                    entry.canvas = null;
                    if (!this._running)
                        delete this._entries[entry.id];
                })
                .catch(() => {})
                .then(() => this._yield())
                .then(next);
        };

        next();
    }

    /**
     * Smart crop of a curated photo for a given aspect ratio (width / height), normalized.
     * Resolves null when the photo was not curated or smartcrop is unavailable. The working canvas
     * is loaded again when the analysis was reused from the photo.
     */
    cropFor(photo, ratio)
    {
        let entry = this._entries[photo.temporaryId];

        if (!entry || !ratio)
            return Promise.resolve(null);

        let key = Number(ratio).toFixed(3);
        entry.crops = entry.crops || {};

        if (entry.crops[key])
            return Promise.resolve(entry.crops[key]);

        let canvas = entry.canvas ? Promise.resolve(entry.canvas) : this.analyzer.load(photo).then((loaded) => { entry.canvas = loaded; return loaded; });

        return canvas
            .then((loaded) => this.cropper.crop(loaded, ratio, entry.faces))
            .then((crop) => {
                entry.crops[key] = crop;
                return crop;
            })
            .catch(() => null);
    }

    facesOf(photo)
    {
        let entry = this._entries[photo.temporaryId];
        return entry ? entry.faces : [];
    }

    /** Frees the working canvases (call once the album is built). */
    release()
    {
        for (let id in this._entries)
            this._entries[id].canvas = null;

        this._entries = {};
    }

    // ---------------------------- analyse ---------------------------- //

    _analyseAll(list, opts)
    {
        let entries = [];
        let index = 0;

        let next = () => {
            if (index >= list.length)
                return Promise.resolve(entries);

            let photo = list[index++];
            this._progress("analysing", Math.round(index * 100 / list.length), index, list.length);

            return this._analysePhoto(photo, opts)
                .then((entry) => { entries.push(entry); })
                .then(() => this._yield())
                .then(next);
        };

        return next();
    }

    _analysePhoto(photo, opts)
    {
        let entry = {
            photo : photo,
            id : photo.temporaryId,
            time : curator.MomentClustering.time(photo),
            canvas : null,
            metrics : null,
            faces : [],
            score : 0,
            reasons : []
        };

        this._entries[entry.id] = entry;

        // analysed already (background analysis when the photo was added, or an earlier curation) :
        // the metrics and faces stored on the photo are reused, nothing is loaded
        if (this.analysed(photo))
        {
            entry.metrics = photo.curation.metrics;
            entry.faces = this._storedFaces(photo);
            entry.score = this._score(entry);
            return Promise.resolve(entry);
        }

        return this.analyzer.load(photo)
            .then((canvas) => {
                entry.canvas = canvas;
                entry.metrics = this.analyzer.analyse(canvas);
                return this._findFaces(entry, opts);
            })
            .catch((e) => {
                entry.error = (e && e.message) ? e.message : String(e);
            })
            .then(() => {
                entry.score = this._score(entry);
                photo.prop("curation", { score : entry.score, metrics : entry.metrics, error : entry.error || null });
                return entry;
            });
    }

    _findFaces(entry, opts)
    {
        let photo = entry.photo;

        // faces already known (an earlier analysis stored with the photo) : nothing to detect
        if (this.facesKnown(photo))
        {
            entry.faces = this._storedFaces(photo);
            return Promise.resolve();
        }

        if (!opts.detectFaces)
            return Promise.resolve();

        // no detector on this browser : the photo still counts as analysed, so that neither the
        // background analysis nor the next curation loads it again
        if (!this.faces.supported || this.faces.failed)
        {
            photo.setNormalizedFaces([], { source : "none" });
            return Promise.resolve();
        }

        return this.faces.detect(entry.canvas)
            .then((faces) => {
                entry.faces = faces;
                photo.setNormalizedFaces(faces, { source : "mediapipe" });
            })
            .catch(() => {}); // detection is a bonus, never a reason to fail the curation
    }

    _storedFaces(photo)
    {
        return (photo.facesPositions || []).map((face) => ({
            x : Number(face.x), y : Number(face.y), width : Number(face.width), height : Number(face.height)
        }));
    }

    /**
     * 0..1 : sharpness weighs most, then exposure, faces and color. Unanalysed photos get a neutral 0.5.
     */
    _score(entry)
    {
        let m = entry.metrics;
        if (!m)
            return 0.5;

        let sharp = Math.max(0, Math.min(1, Math.log10(1 + m.sharpness) / 3));

        let exposure = 1;
        if (m.brightness < 0.15)
            exposure -= (0.15 - m.brightness) * 4;
        if (m.brightness > 0.85)
            exposure -= (m.brightness - 0.85) * 4;
        exposure -= Math.max(0, m.clippedDark - 0.3) + Math.max(0, m.clippedBright - 0.2);
        exposure = Math.max(0, Math.min(1, exposure));

        let color = 0.5 + 0.5 * Math.max(0, Math.min(1, m.colorfulness / 60));

        let faceArea = entry.faces.reduce((sum, face) => sum + face.width * face.height, 0);
        let faces = Math.min(1, entry.faces.length * 0.3 + faceArea * 4);

        let photo = entry.photo;
        let resolution = (photo.width && photo.height && photo.width * photo.height < 500 * 500) ? 0.6 : 1;

        return (0.45 * sharp + 0.25 * exposure + 0.10 * color + 0.20 * faces) * resolution;
    }

    // ---------------------------- select ----------------------------- //

    _select(entries, opts)
    {
        let sorted = this._sortByTime(entries);
        let rejected = [], duplicates = [], kept = [];

        // 1 - technical rejects, the sharpness floor adapts to the set (a soft series is not all blurry)
        let sharpnesses = sorted.filter((e) => e.metrics).map((e) => e.metrics.sharpness).sort((a, b) => a - b);
        let median = sharpnesses.length ? sharpnesses[Math.floor(sharpnesses.length / 2)] : 0;
        let sharpnessFloor = Math.max(opts.minSharpness, median * opts.relativeSharpness);

        let candidates = [];
        for (let i = 0; i < sorted.length; i++)
        {
            let entry = sorted[i];
            let reason = this._rejectReason(entry, sharpnessFloor, opts);

            if (reason)
            {
                entry.reasons.push(reason);
                rejected.push(entry);
            }
            else
                candidates.push(entry);
        }

        // 2 - never drop more than maxRejectRatio of the set : the best rejects come back as candidates
        //     (before the duplicate pass, so a restored shot can still be recognised as a twin)
        let maxRejects = Math.floor(entries.length * opts.maxRejectRatio);
        if (rejected.length > maxRejects)
        {
            rejected.sort((a, b) => b.score - a.score);
            while (rejected.length > maxRejects)
            {
                let entry = rejected.shift();
                entry.reasons = [];
                entry.restored = true;
                candidates.push(entry);
            }
            candidates = this._sortByTime(candidates);
        }

        // 3 - duplicates : the best scored twin survives
        for (let i = 0; i < candidates.length; i++)
        {
            let entry = candidates[i];
            let twin = this._findTwin(entry, kept, opts);

            if (!twin)
            {
                kept.push(entry);
                continue;
            }

            if (entry.score > twin.score)
            {
                kept[kept.indexOf(twin)] = entry;
                twin.reasons.push("duplicate");
                twin.twin = entry;
                duplicates.push(twin);
            }
            else
            {
                entry.reasons.push("duplicate");
                entry.twin = twin;
                duplicates.push(entry);
            }
        }

        if (!kept.length && entries.length)
        {
            let best = entries.slice().sort((a, b) => b.score - a.score)[0];
            best.reasons = [];
            kept.push(best);
            rejected = rejected.filter((e) => e != best);
            duplicates = duplicates.filter((e) => e != best);
        }

        kept = this._sortByTime(kept);

        // the canvases of dropped photos are not needed any more
        rejected.concat(duplicates).forEach((entry) => { if (kept.indexOf(entry) == -1) entry.canvas = null; });

        return {
            kept : kept.map((e) => e.photo),
            rejected : rejected.map((e) => ({ photo : e.photo, reasons : e.reasons })),
            duplicates : duplicates.map((e) => ({ photo : e.photo, reasons : e.reasons, twin : e.twin ? e.twin.photo : null }))
        };
    }

    _rejectReason(entry, sharpnessFloor, opts)
    {
        let photo = entry.photo, m = entry.metrics;

        if (photo.width && photo.height && (photo.width < opts.minSize || photo.height < opts.minSize))
            return "too-small";

        if (!m)
            return null; // could not be analysed : benefit of the doubt

        // colorless, mostly white, nobody in it : a document, a screenshot, a receipt
        if (m.colorfulness < 6 && m.clippedBright > 0.35 && !entry.faces.length)
            return "document";

        // exposure first : a very dark frame has no edges left, it would read as blurry
        if (m.brightness < opts.minBrightness || m.clippedDark > opts.maxClippedDark)
            return "too-dark";

        if (m.brightness > opts.maxBrightness || m.clippedBright > opts.maxClippedBright)
            return "too-bright";

        if (m.sharpness < sharpnessFloor)
            return "blurry";

        return null;
    }

    _findTwin(entry, kept, opts)
    {
        if (!entry.metrics)
            return null;

        let ratio = entry.metrics.width / entry.metrics.height;

        for (let i = kept.length - 1; i >= 0; i--)
        {
            let other = kept[i];
            if (!other.metrics)
                continue;

            // a portrait and a landscape are never the same shot
            let otherRatio = other.metrics.width / other.metrics.height;
            if (Math.abs(ratio - otherRatio) / otherRatio > 0.05)
                continue;

            let distance = curator.ImageAnalyzer.hammingDistance(entry.metrics.hash, other.metrics.hash);

            if (distance <= opts.duplicateDistance)
                return other;

            if (distance <= opts.burstDistance && entry.time && other.time && Math.abs(entry.time - other.time) <= opts.burstSeconds)
                return other;
        }

        return null;
    }

    _pickCover(photos)
    {
        let best = null, bestScore = -1;

        for (let i = 0; i < photos.length; i++)
        {
            let photo = photos[i];
            let score = photo.curation ? photo.curation.score : 0.5;

            if (this.facesOf(photo).length)
                score += 0.15;

            if (photo.width && photo.height && photo.width >= photo.height)
                score += 0.05;

            if (score > bestScore)
            {
                bestScore = score;
                best = photo;
            }
        }

        return best;
    }

    // ---------------------------- helpers ---------------------------- //

    _sortByTime(entries)
    {
        let dated = entries.filter((e) => e.time), undated = entries.filter((e) => !e.time);
        dated.sort((a, b) => a.time - b.time);
        return dated.concat(undated);
    }

    _progress(step, progress, index, total)
    {
        this.trigger("curate-progress", { step : step, progress : progress, index : index, total : total });
    }

    _yield()
    {
        return new Promise((resolve) => setTimeout(resolve, 0));
    }
});
